import { useCallback, useRef, useState } from 'react';

export interface Snapshot<TNode, TEdge> {
  nodes: TNode[];
  edges: TEdge[];
}

interface UseUndoRedoOptions {
  /** Tamanho máximo da pilha. Padrão 50. */
  limit?: number;
}

/**
 * Hook genérico de undo/redo para fluxograma.
 *
 * Uso:
 *  const { push, undo, redo, canUndo, canRedo, reset } = useUndoRedo<Node, Edge>();
 *  push(currentSnapshot)        // após cada mudança "estável" do usuário
 *  const prev = undo()          // devolve o snapshot anterior (ou null)
 *  const next = redo()          // refaz (ou null)
 */
export function useUndoRedo<TNode, TEdge>({ limit = 50 }: UseUndoRedoOptions = {}) {
  const past = useRef<Snapshot<TNode, TEdge>[]>([]);
  const future = useRef<Snapshot<TNode, TEdge>[]>([]);
  const [, force] = useState(0);
  const bump = useCallback(() => force((n) => n + 1), []);

  const push = useCallback(
    (snapshot: Snapshot<TNode, TEdge>) => {
      past.current.push(snapshot);
      if (past.current.length > limit) past.current.shift();
      future.current = [];
      bump();
    },
    [bump, limit],
  );

  const undo = useCallback((current: Snapshot<TNode, TEdge>): Snapshot<TNode, TEdge> | null => {
    if (past.current.length === 0) return null;
    const prev = past.current.pop()!;
    future.current.push(current);
    bump();
    return prev;
  }, [bump]);

  const redo = useCallback((current: Snapshot<TNode, TEdge>): Snapshot<TNode, TEdge> | null => {
    if (future.current.length === 0) return null;
    const next = future.current.pop()!;
    past.current.push(current);
    bump();
    return next;
  }, [bump]);

  /** Reescreve as entradas guardadas (ex.: trocar ids provisórios pelos do banco depois de salvar). */
  const remap = useCallback((fn: (snapshot: Snapshot<TNode, TEdge>) => Snapshot<TNode, TEdge>) => {
    past.current = past.current.map(fn);
    future.current = future.current.map(fn);
  }, []);

  const reset = useCallback(() => {
    past.current = [];
    future.current = [];
    bump();
  }, [bump]);

  return {
    push,
    undo,
    redo,
    reset,
    remap,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
  };
}
